import React, { useState, useEffect, useCallback } from 'react';
import {
  getReservations,
  getReservationStatusLabel,
  getReservationStatusOptions,
  getPaymentStatusLabel,
} from '../services/reservationService';
import { formatDateTime } from '../utils/dateFormat';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import CancelReservationModal from '../components/common/CancelReservationModal';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import '../css/ReservationManagement.css';

const ReservationManagement = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalElements: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ status: '', search: '', startFrom: '', startTo: '' });
  const [searchInput, setSearchInput] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReservations({
        page: pagination.page,
        size: pagination.size,
        status: filters.status,
        search: filters.search,
        startFrom: filters.startFrom ? `${filters.startFrom}T00:00:00` : '',
        startTo: filters.startTo ? `${filters.startTo}T23:59:59` : '',
      });
      setReservations(data.content || []);
      setPagination((prev) => ({
        ...prev,
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
      }));
    } catch (error) {
      showSnackbar(error.message || '예약 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, filters, showSnackbar]);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowClick = (reservation) => {
    setSelectedReservation(reservation);
  };

  const handleClosePanel = () => {
    setSelectedReservation(null);
  };

  const handleOpenCancel = () => {
    setCancelModalOpen(true);
  };

  const handleCancelled = () => {
    setCancelModalOpen(false);
    setSelectedReservation(null);
    showSnackbar('예약이 취소되고 환불 처리되었습니다.', 'success');
    fetchReservations();
  };

  const isCancelable = (status) => status === 'CONFIRMED' || status === 'IN_USE';

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'status-confirmed';
      case 'IN_USE': return 'status-inuse';
      case 'COMPLETED': return 'status-completed';
      case 'CANCELLED': return 'status-cancelled';
      case 'NO_SHOW': return 'status-noshow';
      default: return '';
    }
  };

  return (
    <div className="admin-layout">
      <Header />
      <PageHeader title="예약 관리" />
      <div className={`reservation-management ${selectedReservation ? 'panel-open' : ''}`}>
        <div className="main-content">
          <div className="filters-section">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                placeholder="예약번호, 이름, 전화번호로 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">검색</button>
            </form>

            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              {getReservationStatusOptions().map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <input
              type="date"
              className="filter-date"
              value={filters.startFrom}
              onChange={(e) => handleFilterChange('startFrom', e.target.value)}
            />
            <span className="date-sep">~</span>
            <input
              type="date"
              className="filter-date"
              value={filters.startTo}
              onChange={(e) => handleFilterChange('startTo', e.target.value)}
            />

            <div className="total-count">총 {pagination.totalElements.toLocaleString()}건</div>
          </div>

          <div className="table-container">
            <table className="reservation-table">
              <thead>
                <tr>
                  <th>예약번호</th>
                  <th>상태</th>
                  <th>예약발생</th>
                  <th>이용시간</th>
                  <th>고객</th>
                  <th>매장</th>
                  <th>좌석</th>
                  <th>결제금액</th>
                  <th>쿠폰</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="loading-cell">로딩 중...</td></tr>
                ) : reservations.length === 0 ? (
                  <tr><td colSpan="9" className="empty-cell">예약이 없습니다</td></tr>
                ) : (
                  reservations.map((r) => (
                    <tr
                      key={r.reservationId}
                      className={`clickable-row ${selectedReservation?.reservationId === r.reservationId ? 'selected' : ''}`}
                      onClick={() => handleRowClick(r)}
                    >
                      <td className="mono">{r.reservationNumber}</td>
                      <td>
                        <span className={`status-badge ${statusBadgeClass(r.status)}`}>
                          {getReservationStatusLabel(r.status)}
                        </span>
                      </td>
                      <td>{formatDateTime(r.createdAt)}</td>
                      <td>{formatDateTime(r.startTime)} ~ {formatDateTime(r.endTime)}</td>
                      <td>{r.customerName}<br /><span className="muted">{r.customerPhone || '-'}</span></td>
                      <td>{r.storeName || '-'}</td>
                      <td>{r.seatName || '-'}</td>
                      <td className="right">{Number(r.finalAmount || 0).toLocaleString()}원</td>
                      <td>{r.couponName ? `${r.couponName}` : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => handlePageChange(0)} disabled={pagination.page === 0}>{'<<'}</button>
              <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 0}>{'<'}</button>
              <span className="page-info">{pagination.page + 1} / {pagination.totalPages}</span>
              <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages - 1}>{'>'}</button>
              <button onClick={() => handlePageChange(pagination.totalPages - 1)} disabled={pagination.page >= pagination.totalPages - 1}>{'>>'}</button>
            </div>
          )}
        </div>

        {selectedReservation && (
          <aside className="reservation-detail-panel">
            <div className="panel-header">
              <h2>예약 상세</h2>
              <button className="panel-close" onClick={handleClosePanel}>×</button>
            </div>
            <div className="panel-body">
              <DetailRow label="예약번호" value={selectedReservation.reservationNumber} />
              <DetailRow label="상태" value={getReservationStatusLabel(selectedReservation.status)} />
              <DetailRow label="예약발생시각" value={formatDateTime(selectedReservation.createdAt)} />
              <DetailRow label="시작시간" value={formatDateTime(selectedReservation.startTime)} />
              <DetailRow label="종료시간" value={formatDateTime(selectedReservation.endTime)} />
              <DetailRow label="체크인" value={formatDateTime(selectedReservation.checkedInAt)} />
              <DetailRow label="체크아웃" value={formatDateTime(selectedReservation.checkedOutAt)} />
              <hr className="panel-divider" />
              <DetailRow label="고객 ID" value={selectedReservation.customerId} mono />
              <DetailRow label="고객명" value={selectedReservation.customerName} />
              <DetailRow label="전화번호" value={selectedReservation.customerPhone || '-'} />
              <hr className="panel-divider" />
              <DetailRow label="매장 ID" value={selectedReservation.storeId} mono />
              <DetailRow label="매장명" value={selectedReservation.storeName} />
              <DetailRow label="좌석 ID" value={selectedReservation.seatId} mono />
              <DetailRow label="좌석명" value={selectedReservation.seatName} />
              <hr className="panel-divider" />
              <DetailRow label="원금액" value={`${Number(selectedReservation.originalAmount || 0).toLocaleString()}원`} />
              <DetailRow label="할인금액" value={`${Number(selectedReservation.discountAmount || 0).toLocaleString()}원`} />
              <DetailRow label="결제금액" value={`${Number(selectedReservation.finalAmount || 0).toLocaleString()}원`} />
              <DetailRow label="결제수단" value={selectedReservation.paymentMethod || '-'} />
              <DetailRow label="결제상태" value={getPaymentStatusLabel(selectedReservation.paymentStatus)} />
              <DetailRow label="사용 쿠폰" value={selectedReservation.couponName ? `${selectedReservation.couponName} (${selectedReservation.couponCode})` : '-'} />
              {selectedReservation.cancelledAt && (
                <>
                  <hr className="panel-divider" />
                  <DetailRow label="취소시각" value={formatDateTime(selectedReservation.cancelledAt)} />
                  <DetailRow label="취소사유" value={selectedReservation.cancelReason || '-'} />
                </>
              )}
            </div>
            <div className="panel-footer">
              <button
                className="panel-cancel-btn"
                disabled={!isCancelable(selectedReservation.status)}
                onClick={handleOpenCancel}
                title={!isCancelable(selectedReservation.status) ? '취소 가능한 상태가 아닙니다' : ''}
              >
                예약 취소 및 환불
              </button>
            </div>
          </aside>
        )}
      </div>

      {cancelModalOpen && selectedReservation && (
        <CancelReservationModal
          reservation={selectedReservation}
          onClose={() => setCancelModalOpen(false)}
          onCancelled={handleCancelled}
          onError={(msg) => showSnackbar(msg, 'error')}
        />
      )}

      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isOpen={snackbar.isOpen}
        onClose={hideSnackbar}
        duration={3000}
      />
    </div>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className={`detail-value ${mono ? 'mono' : ''}`}>{value || '-'}</span>
  </div>
);

export default ReservationManagement;